import React, { useEffect, useRef, useState } from 'react';

/**
 * HLSVideoPlayer
 * 
 * Plays HLS streams (.m3u8) using hls.js for smooth mobile streaming.
 * Falls back to native <video> src for non-HLS or unsupported browsers.
 * 
 * Props:
 *   hlsUrl       - CloudFront URL to .m3u8 manifest (preferred)
 *   fallbackUrl  - Direct video URL (used if no hlsUrl or HLS unsupported)
 *   poster       - Thumbnail URL shown before play
 *   style        - Additional styles for the video element
 *   onError      - Optional error callback
 */
const HLSVideoPlayer = ({ hlsUrl, fallbackUrl, poster, style = {}, onError }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const srcUrl = hlsUrl || fallbackUrl;
    if (!srcUrl) return;

    // If HLS url and browser doesn't natively support HLS — use hls.js
    if (hlsUrl && !video.canPlayType('application/vnd.apple.mpegurl')) {
      setLoading(true);

      // Dynamically load hls.js from CDN to avoid bundle bloat
      const loadHls = () => {
        return new Promise((resolve, reject) => {
          if (window.Hls) return resolve(window.Hls);
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/hls.js/1.5.7/hls.min.js';
          script.onload = () => resolve(window.Hls);
          script.onerror = reject;
          document.head.appendChild(script);
        });
      };

      loadHls().then(Hls => {
        if (!Hls.isSupported()) {
          // HLS.js not supported — fall back to direct URL
          video.src = fallbackUrl || hlsUrl;
          setLoading(false);
          return;
        }

        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          backBufferLength: 30,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          startLevel: -1,        // auto quality selection
          abrEwmaDefaultEstimate: 500000  // start conservative on mobile
        });

        hls.loadSource(hlsUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setLoading(false);
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            console.error('HLS fatal error:', data.type, data.details);
            setError('Video playback error. Trying fallback...');
            hls.destroy();
            // Fall back to direct URL
            if (fallbackUrl) {
              video.src = fallbackUrl;
              setError(null);
            }
            if (onError) onError(data);
          }
        });

        hlsRef.current = hls;
      }).catch(err => {
        console.error('Failed to load hls.js:', err);
        video.src = fallbackUrl || hlsUrl;
        setLoading(false);
      });

    } else {
      // Native HLS support (Safari/iOS) or no HLS url — use src directly
      video.src = hlsUrl || fallbackUrl;
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [hlsUrl, fallbackUrl]);

  const defaultStyle = {
    width: '100%',
    borderRadius: '10px',
    background: '#000',
    display: 'block',
    ...style
  };

  return (
    <div style={{ position: 'relative' }}>
      {loading && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.6)', borderRadius: '10px', zIndex: 1
        }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '50%',
            border: '3px solid rgba(255,255,255,0.3)',
            borderTopColor: '#fff',
            animation: 'spin 0.8s linear infinite'
          }} />
        </div>
      )}
      {error && (
        <div style={{
          position: 'absolute', top: '8px', left: '8px', right: '8px',
          background: 'rgba(239,68,68,0.9)', color: '#fff',
          padding: '6px 10px', borderRadius: '6px', fontSize: '12px', zIndex: 2
        }}>
          {error}
        </div>
      )}
      <video
        ref={videoRef}
        controls
        playsInline
        poster={poster}
        style={defaultStyle}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default HLSVideoPlayer;
